import { Link, useLocation } from "react-router";
import { useTranslation } from "@ncp/i18n";
import { ChevronRight, Home } from "lucide-react";
import { ALL_NAV_ITEMS } from "../app/nav";
import { useConsole, type Crumb } from "../app/console-context";

/**
 * Route-derived breadcrumbs: Console › Section › (page-provided crumbs). The section comes from the sidebar item whose href prefixes the
 * current path; detail pages append their own segments with `useBreadcrumbs`.
 */
export function Breadcrumbs() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { crumbs } = useConsole();

  const section = ALL_NAV_ITEMS.filter((i) => i.href !== "/" && pathname.startsWith(i.href)).sort((a, b) => b.href.length - a.href.length)[0];
  if (!section && pathname === "/") return null;

  const items: Crumb[] = [];
  const sectionLabel = section ? t(`admin:nav.${section.label}`) : null;
  if (section) items.push({ label: sectionLabel!, href: section.href });
  if (crumbs?.length) {
    // Pages usually repeat their section as the first crumb; collapse it so list pages read "Console › Forms", not "Console › Forms › Forms".
    const first = crumbs[0];
    const repeatsSection = section && (first.href === section.href || (!first.href && first.label === sectionLabel));
    items.push(...(repeatsSection ? crumbs.slice(1) : crumbs));
  }
  else if (section && pathname !== section.href) {
    // Unknown deeper path: show the trailing segment so the user still sees where they are.
    const tail = pathname.slice(section.href.length).split("/").filter(Boolean);
    if (tail.length) items.push({ label: decodeURIComponent(tail[tail.length - 1]) });
  }
  if (items.length === 0) return null;

  return (
    <nav aria-label={t("admin:shell.breadcrumbs")} className="mb-3 flex items-center gap-1 text-xs text-sand-600">
      <Link to="/" className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 transition-colors hover:bg-sand-100 hover:text-foreground">
        <Home className="size-3.5" />
        <span className="sr-only sm:not-sr-only">{t("admin:nav.overview")}</span>
      </Link>
      {items.map((c, i) => {
        const last = i === items.length - 1;
        return (
          <span key={`${c.label}-${i}`} className="inline-flex items-center gap-1">
            <ChevronRight className="size-3.5 text-sand-400 rtl:rotate-180" aria-hidden />
            {c.href && !last ? (
              <Link to={c.href} className="rounded-md px-1 py-0.5 transition-colors hover:bg-sand-100 hover:text-foreground">
                {c.label}
              </Link>
            ) : (
              <span className={last ? "px-1 py-0.5 font-medium text-foreground" : "px-1 py-0.5"} aria-current={last ? "page" : undefined}>
                {c.label}
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
}
